import React, { useEffect, useState, useMemo } from 'react';
import { Container, CircularProgress, Box, Typography } from '@mui/material';
import MapView from '../MapView';
import LocationErrorDialog from '../dialog/LocationErrorDialog';
import DistanceFilter from '../filter/DistanceFilter';
import StopSummaryPanel from '../stop/StopSummaryPanel';
import ServiceTypeFilter from '../filter/ServiceTypeFilter';
import StopInformationPanel from '../stop/StopInformationPanel';
import { useAppState } from '../../context/AppStateProvider';
import { rapidRailBusService } from '../../services/rapidRailBusService';
import { rapidRailKLService } from '../../services/rapidRailKLService';
import { RapidRailKLType } from '../../enum/serviceType';
import { filterStopsByDistance } from '../../utils/stopUtil';

const DEFAULT_SERVICE_TYPES = ['BUS'];

export function NearbyStopPage() {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [distance, setDistance] = useState(1);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(DEFAULT_SERVICE_TYPES);

  const {
    selectedStop, setSelectedStop, setStopsTimes, setTrips, setRoutes, setShapes,
    stops, setStops, setFrequencies
  } = useAppState();

  const [shapesByService, setShapesByService] = useState({ RAPIDKL: [], BUS: [] });
  const [stopsByService, setStopsByService] = useState({ LRT: [], MRT: [], BRT: [], BUS: [] });
  const [stopTimesByService, setStopTimeByService] = useState({ RAPIDKL: [], BUS: [] });
  const [tripsByService, setTripsByService] = useState({ RAPIDKL: [], BUS: [] });
  const [routesByService, setRoutesByService] = useState({ RAPIDKL: [], BUS: [] });
  const [frequencyByService, setFrequencyByService] = useState({ RAPIDKL: [], BUS: [] });

  const { filteredStopsByService, overlappingStops } = useMemo(() => {
    if (!location) return { filteredStopsByService: {}, overlappingStops: [] };

    const filtered = {};
    const coordMap = new Map();

    Object.entries(stopsByService).forEach(([serviceType, stops]) => {
      if (selectedServiceTypes.includes(serviceType)) {
        const nearbyStops = filterStopsByDistance(location, stops, distance);
        filtered[serviceType] = nearbyStops;

        nearbyStops.forEach(stop => {
          const key = `${stop.stop_lat},${stop.stop_lon}`;
          if (!coordMap.has(key)) coordMap.set(key, []);
          coordMap.get(key).push({ ...stop, serviceType });
        });
      }
    });

    const overlapping = Array.from(coordMap.entries())
      .filter(([, stops]) => stops.length > 1)
      .map(([coord, stops]) => ({
        coord: coord.split(',').map(Number),
        stops,
      }));

    return { filteredStopsByService: filtered, overlappingStops: overlapping };
  }, [location, distance, stopsByService, selectedServiceTypes]);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setLocationError(true);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setLocation(userLocation);

        try {
          setLoading(true);
          await Promise.all([
            fetchAllStops(),
            fetchDataForService('getStopTimes', setStopTimeByService, setStopsTimes),
            fetchDataForService('getTrips', setTripsByService, setTrips),
            fetchDataForService('getRoutes', setRoutesByService, setRoutes),
            fetchDataForService('getShapes', setShapesByService, setShapes),
            fetchFrequencies()
          ]);
        } catch (err) {
          console.error('Failed to fetch initial data:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Location permission denied:', err);
        setLocationError(true);
        setLoading(false);
      }
    );
  }, []);

  async function fetchAllStops() {
    try {
      const [busStops, rapidRailKLStops] = await Promise.all([
        rapidRailBusService.getStops(),
        rapidRailKLService.getStops()
      ]);

      const groupedStops = { LRT: [], MRT: [], BRT: [] };
      rapidRailKLStops.forEach(stop => {
        if (groupedStops[stop.category]) {
          groupedStops[stop.category].push(stop);
        }
      });

      const stops = {
        ...groupedStops,
        BUS: busStops
      };

      setStopsByService(stops);
      setStops(stops);
    } catch (error) {
      console.error("Failed to load stops:", error);
    }
  }

  async function fetchDataForService(serviceFnName, stateSetter, contextSetter, defaultValue = { RAPIDKL: [], BUS: [] }) {
    try {
      const [bus, rapid] = await Promise.all([
        rapidRailBusService[serviceFnName](),
        rapidRailKLService[serviceFnName]()
      ]);
      const result = { RAPIDKL: rapid, BUS: bus };
      stateSetter(result);
      contextSetter(result);
    } catch (error) {
      console.error(`Failed to load ${serviceFnName}:`, error);
      stateSetter(defaultValue);
      contextSetter(defaultValue);
    }
  }

  async function fetchFrequencies() {
    try {
      const bus = await rapidRailBusService.getFrequencies();
      const result = { RAPIDKL: [], BUS: bus };
      setFrequencyByService(result);
      setFrequencies(result);
    } catch (error) {
      console.error("Failed to load frequencies:", error);
      setFrequencyByService({ RAPIDKL: [], BUS: [] });
      setFrequencies({ RAPIDKL: [], BUS: [] });
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading, please wait...
        </Typography>
      </Box>
    );
  }

  return (
    <Container disableGutters maxWidth={false}>
      {locationError ? (
        <LocationErrorDialog />
      ) : (
        <Box sx={{ position: 'relative' }}>
          <MapView
            userLocation={location}
            distanceFilter={distance}
            filteredStops={filteredStopsByService}
            overlappingStops={overlappingStops}
          />

          {selectedStop ? (
            <StopInformationPanel
              userLocation={location}
              stop={selectedStop}
              onClose={() => setSelectedStop(null)}
            />
          ) : (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 0,
                  width: '100%',
                  zIndex: 1000,
                  p: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: { xs: 'stretch', sm: 'center' },
                }}
              >
                <DistanceFilter distance={distance} setDistance={setDistance} />
                <ServiceTypeFilter
                  selectedTypes={selectedServiceTypes}
                  setSelectedTypes={setSelectedServiceTypes}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: 0,
                  right: 0,
                  zIndex: 1100,
                  p: 1,
                  width: '100%',
                }}
              >
                <StopSummaryPanel stops={filteredStopsByService} />
              </Box>
            </>
          )}
        </Box>
      )}
    </Container>
  );
}

export default NearbyStopPage;
